// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, Button, Typography, Modal, Box } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabel, Loading } from '~/components/common';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { convertQuoteToOrder } from '~/thunks/quote/quoteThunk';
import { IQuoteDetail } from '~/utils/interface/quotes';
import { PaymentTermsEnum, RoleEnum } from '~/utils/enum/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './ConvertOrderFormModal.module.scss';
import { icons } from '~/assets';

type Props = {
  detailQuote?: IQuoteDetail;
  onShowModal?: () => void;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ConvertOrderFormModal = (props: Props) => {
  //#region Destructuring Props
  const { detailQuote, onShowModal, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const { id: quoteId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleConvertToOrder = () => {
    if (!quoteId) return;

    setIsLoading(true);

    dispatch(convertQuoteToOrder(quoteId))
      .unwrap()
      .then(() => {
        handleNavigateToOrder();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNavigateToOrder = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.order);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.order);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.order);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.order);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.order);
        break;
    }
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };
  //#endregion Handle Function

  return (
    <Modal open={true} onClose={handleCloseModal}>
      <>
        <Box id='convertOrderFormModalComponent' className={cx('container')}>
          <Stack direction={'column'} alignItems={'center'} gap={'10px'}>
            <Typography className={cx('titleConvert')}>{t('convert_quote_title_convert')}</Typography>

            <Typography className={cx('title')}>
              {t(
                detailQuote?.paymentTerms === PaymentTermsEnum.NET_30
                  ? t('convert_quote_title_detail_convert_net')
                  : t('convert_quote_title_detail_convert', {
                      value: formatCurrencyUSD({
                        value:
                          detailQuote?.paymentTerms === PaymentTermsEnum.FULL_PREPAYMENT
                            ? detailQuote?.total
                            : detailQuote?.depositMadeAmount,
                        showDecimal: true,
                      }),
                    })
              )}

              {detailQuote?.paymentTerms === PaymentTermsEnum.NET_30 && (
                <Typography className={cx('description')}>{t('convert_quote_title_detail_description_net')}</Typography>
              )}
            </Typography>
          </Stack>

          <Stack direction={'row'} justifyContent={'center'} marginTop={'30px'} gap={'12px'} alignItems={'center'}>
            {detailQuote?.paymentTerms === PaymentTermsEnum.NET_30 && (
              <Button variant='outlined' className={cx('buttonConvert')} onClick={handleConvertToOrder}>
                {t('convert_quote_title_convert')}
              </Button>
            )}

            <Button variant='contained' className={cx('buttonCollectDeposit')} onClick={() => onShowModal?.()}>
              {t('convert_quote_title_collect_deposit')}
            </Button>
          </Stack>
        </Box>

        {isLoading && <Loading />}
      </>
    </Modal>
  );
};

export default ConvertOrderFormModal;
